// ----------------------------------------------------------------------

/**
 * One checkbox everywhere: an 18px rounded square drawn in CSS, so its colours
 * come from the palette in both modes. MUI's own was a 24px Material glyph — a
 * sharp-cornered outline in a 42px circle of hover — that read as a different
 * product next to the rounded fields and buttons.
 *
 * Unchecked: the underline border (3:1 on every surface, as a control edge must
 * be) over the paper. Checked and indeterminate: filled with the control's
 * colour, a tick or a bar in its contrast text. A checkbox given its own `icon`
 * (the colour picker) keeps it: these are only defaults.
 */

const BOX = 'CabinetCheckbox-box';

const Tick = () => (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path
            d="M3.5 8.25 6.5 11.25 12.5 4.75"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const Bar = () => (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path d="M4 8h8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

export default function Checkbox(theme) {
    const tone = (color) =>
        theme.palette[color && color !== 'default' ? color : 'primary'];

    return {
        MuiCheckbox: {
            defaultProps: {
                disableRipple: true,
                icon: <span className={BOX} />,
                checkedIcon: (
                    <span className={BOX}>
                        <Tick />
                    </span>
                ),
                indeterminateIcon: (
                    <span className={BOX}>
                        <Bar />
                    </span>
                ),
            },
            styleOverrides: {
                root: ({ ownerState }) => {
                    const { main, contrastText } = tone(ownerState.color);
                    const size = ownerState.size === 'small' ? 16 : 18;
                    return {
                        // A rounded square of hover around a rounded square,
                        // not MUI's circle.
                        'padding': 8,
                        'borderRadius': theme.shape.borderRadius,
                        [`& .${BOX}`]: {
                            boxSizing: 'border-box',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: size,
                            height: size,
                            borderRadius: size > 16 ? 5 : 4,
                            border: `1.5px solid ${theme.palette.border.underline}`,
                            backgroundColor: theme.palette.background.paper,
                            color: contrastText,
                            transition: theme.transitions.create(
                                ['background-color', 'border-color'],
                                { duration: theme.transitions.duration.shortest }
                            ),
                            '& svg': { width: '100%', height: '100%' },
                        },
                        '&:hover': { backgroundColor: theme.palette.action.hover },
                        [`&:hover .${BOX}`]: { borderColor: main },
                        [`&.Mui-checked .${BOX}, &.MuiCheckbox-indeterminate .${BOX}`]: {
                            borderColor: main,
                            backgroundColor: main,
                        },
                        '&.Mui-disabled': { backgroundColor: 'transparent' },
                        [`&.Mui-disabled .${BOX}`]: {
                            borderColor: theme.palette.action.disabled,
                            backgroundColor: theme.palette.action.disabledBackground,
                        },
                        [`&.Mui-disabled.Mui-checked .${BOX}, &.Mui-disabled.MuiCheckbox-indeterminate .${BOX}`]: {
                            borderColor: 'transparent',
                            backgroundColor: theme.palette.action.disabled,
                            color: theme.palette.background.paper,
                        },
                    };
                },
            },
        },
    };
}
